/**
 * Offline Detection & Sync Handler
 * Shows offline banner and manages offline action queue
 */

import { useState, useEffect } from 'react';

// Storage key for offline actions queue
const OFFLINE_QUEUE_KEY = 'offline_actions_queue';

/**
 * Custom hook for offline detection
 */
export function useOfflineDetector() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      console.log('[Offline] Back online');
      setIsOnline(true);

      // Trigger sync if we were offline
      if (!navigator.onLine) return;

      // Request background sync if available
      if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.sync.register('sync-offline-actions').catch(console.error);
        });
      } else {
        // Fallback: sync manually
        syncOfflineActions();
      }
    };

    const handleOffline = () => {
      console.log('[Offline] Gone offline');
      setIsOnline(false);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    setIsOnline(navigator.onLine);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isOnline, wasOffline };
}

/**
 * Queue an action to be performed when back online
 */
export function queueOfflineAction(action) {
  try {
    const queue = JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || '[]');
    queue.push({
      id: Date.now(),
      timestamp: new Date().toISOString(),
      ...action
    });
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
    console.log('[Offline] Action queued:', action.type);
  } catch (error) {
    console.error('[Offline] Failed to queue action:', error);
  }
}

/**
 * Get all queued offline actions
 */
export function getOfflineQueue() {
  try {
    return JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || '[]');
  } catch {
    return [];
  }
}

/**
 * Clear the offline queue
 */
export function clearOfflineQueue() {
  localStorage.removeItem(OFFLINE_QUEUE_KEY);
}

/**
 * Sync offline actions
 */
export async function syncOfflineActions() {
  const queue = getOfflineQueue();
  if (queue.length === 0) return;

  console.log('[Offline] Syncing', queue.length, 'queued actions');

  const failedActions = [];

  for (const action of queue) {
    try {
      if (action.type === 'api_call') {
        await fetch(action.url, {
          method: action.method || 'POST',
          headers: action.headers || { 'Content-Type': 'application/json' },
          body: action.body ? JSON.stringify(action.body) : undefined
        });
      } else if (action.type === 'firestore_write') {
        // Handle Firestore writes - would need to import firestore here
        console.log('[Offline] Firestore sync not implemented in this module');
        failedActions.push(action);
      }
    } catch (error) {
      console.error('[Offline] Failed to sync action:', action.id, error);
      failedActions.push(action);
    }
  }

  // Keep failed actions for retry
  if (failedActions.length > 0) {
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(failedActions));
  } else {
    clearOfflineQueue();
  }

  console.log('[Offline] Sync complete. Failed:', failedActions.length);
}

/**
 * Wrapper for fetch that queues on offline
 */
export async function offlineFetch(url, options = {}) {
  if (!navigator.onLine) {
    // Queue the request for later
    queueOfflineAction({
      type: 'api_call',
      url,
      method: options.method || 'GET',
      headers: options.headers,
      body: options.body
    });

    // Return a fake response or throw
    throw new Error('Offline - request queued');
  }

  return fetch(url, options);
}

/**
 * Check if a specific page/route is cached
 */
export async function isRouteCached(path) {
  if (!('caches' in window)) return false;

  try {
    const cache = await caches.open('scope180-cache-v1');
    const response = await cache.match(path);
    return !!response;
  } catch {
    return false;
  }
}

/**
 * Prefetch and cache specific routes
 */
export async function prefetchRoutes(routes) {
  if (!('caches' in window) || !('serviceWorker' in navigator)) return;

  try {
    const cache = await caches.open('scope180-cache-v1');
    await cache.addAll(routes);
    console.log('[Offline] Prefetched routes:', routes);
  } catch (error) {
    console.error('[Offline] Failed to prefetch routes:', error);
  }
}

/**
 * Get connection quality info
 */
export function getConnectionInfo() {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  if (!connection) {
    return { type: 'unknown', effectiveType: 'unknown', downlink: null, rtt: null };
  }

  return {
    type: connection.type,
    effectiveType: connection.effectiveType,
    downlink: connection.downlink,
    rtt: connection.rtt,
    saveData: connection.saveData
  };
}

/**
 * Check if connection is slow
 */
export function isSlowConnection() {
  const info = getConnectionInfo();
  return info.effectiveType === 'slow-2g' || info.effectiveType === '2g';
}

const offlineDetector = {
  useOfflineDetector,
  queueOfflineAction,
  getOfflineQueue,
  clearOfflineQueue,
  syncOfflineActions,
  offlineFetch,
  isRouteCached,
  prefetchRoutes,
  getConnectionInfo,
  isSlowConnection
};

export default offlineDetector;
